import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { MISSION_STATUSES, SERVICES, serviceName } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { ReceiptUploader } from "../../components/ops/ReceiptUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, MapPin, Megaphone, Sparkles, Loader2, Trash2, Check, Camera, CheckCircle2 } from "lucide-react";

export default function Missions() {
  const location = useLocation();
  const [missions, setMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [board, setBoard] = useState({ payout: "", positions: 2 });
  const [aiSuggest, setAiSuggest] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", service_type: "moving", pickup_address: "", dropoff_address: "", scheduled_date: "", scheduled_time: "09:00", value: 0 });

  const load = () => {
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/resources/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/resources/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Auto-open the mission passed in via navigation state (e.g. from Calendar → Open Job).
  useEffect(() => {
    const focusId = location.state?.focusMissionId;
    if (!focusId) return;
    api.get(`/missions/${focusId}`).then((r) => setDetail(r.data)).catch(() => {});
    window.history.replaceState({}, "");
  }, [location.state]);

  const remove = async (id) => {
    if (!window.confirm("Delete this mission? This can't be undone.")) return;
    try {
      await api.delete(`/missions/${id}`);
      toast.success("Mission deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const [selected, setSelected] = useState(new Set());
  const [receiptOpen, setReceiptOpen] = useState(false);
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} mission${selected.size > 1 ? "s" : ""}? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/missions/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} mission${data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const patch = async (id, updates) => {
    try {
      const { data } = await api.patch(`/missions/${id}`, updates);
      setDetail(data);
      setMissions((ms) => ms.map((m) => (m.id === id ? data : m)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleChecklist = (i) => {
    const checklist = detail.checklist.map((c, idx) => (idx === i ? { ...c, done: !c.done } : c));
    patch(detail.id, { checklist });
  };

  const toggleCrew = (empId) => {
    const crew = detail.crew.includes(empId) ? detail.crew.filter((c) => c !== empId) : [...detail.crew, empId];
    patch(detail.id, { crew });
  };

  const suggested = detail && pricing
    ? Math.max(25, Math.round(((detail.value || 0) * (pricing.payout_percent || 30)) / 100 / (Number(board.positions) || 1)))
    : 0;

  const postToBoard = async () => {
    try {
      const { data } = await api.post(`/missions/${detail.id}/post-to-board`, {
        payout_per_helper: Number(board.payout) || suggested,
        open_positions: Number(board.positions) || 1,
      });
      setDetail(data);
      setMissions((ms) => ms.map((m) => (m.id === data.id ? data : m)));
      toast.success("Posted to job board — all crew notified");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = (m) => {
    setAiSuggest(null);
    setDetail(m);
    api.get(`/missions/${m.id}`).then((r) => setDetail(r.data)).catch(() => {});
  };

  const aiDispatch = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/missions/${detail.id}/ai-dispatch`);
      setAiSuggest(data);
    } catch (e) { toast.error(errMsg(e)); }
    setAiLoading(false);
  };

  const createMission = async () => {
    try {
      await api.post("/missions", { ...form, value: Number(form.value) || 0 });
      toast.success("Mission created");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = filter === "all" ? missions : missions.filter((m) => m.status === filter);

  return (
    <div data-testid="ops-missions-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Job Management</p>
          <h1 className="page-title mt-1.5">Mission Board</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="missions-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {MISSION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="missions-new-button" onClick={() => setCreateOpen(true)} className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> New Mission
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div data-testid="missions-bulk-bar" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold text-red-500">{selected.size} selected</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setSelected(new Set())} data-testid="missions-bulk-clear">Clear</Button>
            <Button size="sm" onClick={bulkDelete} data-testid="missions-bulk-delete"
              className="rounded-full h-8 bg-red-500 hover:bg-red-600 text-white font-semibold">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((m) => (
          <div key={m.id} data-testid={`mission-card-${m.mission_number}`}
            className={`text-left rounded-xl border bg-card p-5 card-lift relative ${selected.has(m.id) ? "border-[var(--cc-accent)] ring-1 ring-[var(--cc-accent)]" : ""}`}>
            <span role="button" tabIndex={0} className="absolute top-3 left-3 z-10 p-1 cursor-pointer" data-testid={`mission-select-${m.mission_number}`}
              onClick={(e) => { e.stopPropagation(); toggleOne(m.id); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggleOne(m.id); } }}
              aria-label={selected.has(m.id) ? "Unselect" : "Select"}>
              <Checkbox checked={selected.has(m.id)} className="pointer-events-none" />
            </span>
            <button onClick={() => openDetail(m)} className="text-left w-full pl-7">
              <div className="flex items-center justify-between">
                <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number}</p>
                <div className="flex items-center gap-1.5">
                  {m.priority && <StatusBadge status="priority" />}
                  <StatusBadge status={m.status} />
                </div>
              </div>
              <p className="font-heading font-semibold mt-2">{m.customer_name}</p>
              <p className="text-xs text-muted-foreground">{serviceName(m.service_type)} · {fmtDate(m.scheduled_date)} {m.scheduled_time}</p>
              <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {(m.pickup_address || "TBD").split(",")[0]}</span>
                <span className="font-mono-cc text-foreground font-semibold">{fmtMoney(m.value)}</span>
              </div>
              <div className="mt-3 flex gap-1">
                {m.checklist?.map((c, i) => <span key={i} className={`h-1 flex-1 rounded-full ${c.done ? "bg-[var(--cc-accent)]" : "bg-secondary"}`} />)}
              </div>
            </button>
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No missions match this filter.</p>}
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="mission-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading flex items-center gap-3">
                  <span className="font-mono-cc text-[var(--cc-accent)]">{detail.mission_number}</span> {detail.customer_name}
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-6 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3"><p className="overline-cc">Service</p><p className="mt-1 font-semibold">{serviceName(detail.service_type)}</p></div>
                  <div className="rounded-lg border p-3"><p className="overline-cc">Value</p><p className="mt-1 font-semibold">{fmtMoney(detail.value)}</p></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={detail.status} onValueChange={(v) => patch(detail.id, { status: v })}>
                    <SelectTrigger data-testid="mission-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{MISSION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input type="date" value={detail.scheduled_date || ""} onChange={(e) => patch(detail.id, { scheduled_date: e.target.value })} data-testid="mission-date-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <Input type="time" value={detail.scheduled_time || ""} onChange={(e) => patch(detail.id, { scheduled_time: e.target.value })} data-testid="mission-time-input" />
                  </div>
                </div>
                <div className="space-y-1">
                  {detail.pickup_address && <p className="flex gap-2"><MapPin className="w-4 h-4 text-[var(--cc-accent)] mt-0.5 shrink-0" /> {detail.pickup_address}</p>}
                  {detail.dropoff_address && <p className="flex gap-2"><MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" /> {detail.dropoff_address}</p>}
                </div>
                <div className="rounded-lg border bg-secondary/40 p-3 text-xs" data-testid="mission-unit-summary">
                  <span className="font-semibold text-foreground">Unit: </span>
                  <span className="text-muted-foreground">
                    {detail.crew.length ? detail.crew.map((id) => employees.find((e) => e.id === id)?.name).filter(Boolean).join(" + ") : "No crew"}
                    {" · "}{vehicles.find((v) => v.id === detail.vehicle_id)?.name || "No vehicle"}
                  </span>
                </div>

                {/* PAYMENT — big buttons, iOS-style */}
                <div className="rounded-xl border-2 border-[var(--cc-accent)]/30 bg-[var(--cc-accent)]/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="stat-label">Payment</p>
                      <p className="font-heading font-extrabold text-2xl mt-0.5">
                        {detail.paid_full ? <span className="text-emerald-500">Paid · {fmtMoney(detail.value)}</span> :
                         detail.deposit_paid ? <span className="text-amber-500">Deposit paid</span> :
                         <span className="text-muted-foreground">Awaiting payment</span>}
                      </p>
                    </div>
                    {detail.paid_full && <CheckCircle2 className="w-8 h-8 text-emerald-500" />}
                  </div>
                  {!detail.paid_full && (
                    <Button data-testid="mission-upload-receipt-button"
                      onClick={() => setReceiptOpen(true)}
                      className="w-full h-12 rounded-full bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold btn-glow">
                      <Camera className="w-4 h-4 mr-2" /> Upload Receipt / Mark Paid
                    </Button>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline-cc">Crew Assignment</p>
                    <Button data-testid="ai-dispatch-button" size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={aiDispatch} disabled={aiLoading}>
                      {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1 text-[var(--cc-accent)]" /> AI Suggest</>}
                    </Button>
                  </div>
                  {aiSuggest && (
                    <div className="rounded-lg border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 p-3 mb-3 space-y-2" data-testid="ai-dispatch-suggestion">
                      <p className="text-xs font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[var(--cc-accent)]" /> AI Dispatcher recommends</p>
                      <p className="text-xs"><span className="text-muted-foreground">Crew:</span> {aiSuggest.crew.map((c) => c.name).join(" + ")}</p>
                      <p className="text-xs"><span className="text-muted-foreground">Vehicle:</span> {aiSuggest.vehicle?.name || "None needed"}</p>
                      {aiSuggest.reasoning && <p className="text-[11px] text-muted-foreground italic">{aiSuggest.reasoning}</p>}
                      <div className="flex gap-2 pt-1">
                        <Button data-testid="ai-dispatch-apply" size="sm" className="flex-1 rounded-full h-7 text-xs bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold"
                          onClick={() => { patch(detail.id, { crew: aiSuggest.crew.map((c) => c.id), vehicle_id: aiSuggest.vehicle?.id || null }); setAiSuggest(null); toast.success("AI assignment applied"); }}>
                          Apply
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs" onClick={() => setAiSuggest(null)} data-testid="ai-dispatch-dismiss">Dismiss</Button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5 max-h-56 overflow-y-auto rounded-xl border p-2">
                    {employees.map((e) => {
                      const on = detail.crew.includes(e.id);
                      const dot = e.status === "available" ? "bg-emerald-500" : e.status === "on_mission" ? "bg-teal-400" : "bg-slate-500";
                      return (
                        <button key={e.id} type="button" onClick={() => toggleCrew(e.id)} data-testid={`mission-crew-${e.id}`}
                          className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors duration-200 text-left ${on ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10" : "border-transparent hover:bg-secondary"}`}>
                          <span className="relative shrink-0">
                            <span className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-sm ${on ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)]" : "bg-secondary border"}`}>
                              {e.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                            </span>
                            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${dot}`} title={e.status} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-semibold text-sm truncate">{e.name}</span>
                            <span className="block text-[11px] text-muted-foreground">{e.role} · {(e.status || "off duty").replace(/_/g, " ")}</span>
                          </span>
                          {on && <Check className="w-4 h-4 text-[var(--cc-accent)] shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Vehicle</Label>
                  <Select value={detail.vehicle_id || "none"} onValueChange={(v) => patch(detail.id, { vehicle_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="mission-vehicle-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-lg border p-4 space-y-3" data-testid="mission-board-section">
                  <div className="flex items-center justify-between">
                    <p className="overline-cc flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5 text-[var(--cc-accent)]" /> Gig Job Board</p>
                    {detail.board_status && <StatusBadge status={detail.board_status} />}
                  </div>
                  {detail.board_status && (
                    <p className="text-xs text-muted-foreground" data-testid="mission-board-info">
                      Posted at {fmtMoney(detail.payout_per_helper)}/helper · {detail.open_positions} spot{detail.open_positions !== 1 ? "s" : ""} remaining
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Payout per helper ($)</Label>
                      <Input data-testid="board-payout-input" type="number" value={board.payout}
                        onChange={(e) => setBoard({ ...board, payout: e.target.value })}
                        placeholder={suggested ? String(suggested) : "150"} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Open spots</Label>
                      <Input data-testid="board-positions-input" type="number" min={1} value={board.positions}
                        onChange={(e) => setBoard({ ...board, positions: e.target.value })} />
                    </div>
                  </div>
                  {pricing && suggested > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Suggested: {fmtMoney(suggested)}/helper ({pricing.payout_percent}% of job value split across spots)
                    </p>
                  )}
                  <Button data-testid="board-post-button" onClick={postToBoard} size="sm"
                    disabled={!["booked", "confirmed", "assigned"].includes(detail.status)}
                    className="w-full rounded-full bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold disabled:opacity-50">
                    {detail.board_status ? "Update Board Posting" : "Post to Job Board"}
                  </Button>
                </div>
                <div>
                  <p className="overline-cc mb-2">Completion Checklist</p>
                  <div className="space-y-1.5">
                    {detail.checklist?.map((c, i) => (
                      <label key={i} className="flex items-center gap-2.5 py-1 cursor-pointer" data-testid={`mission-checklist-${i}`}>
                        <Checkbox checked={c.done} onCheckedChange={() => toggleChecklist(i)} />
                        <span className={c.done ? "line-through text-muted-foreground" : ""}>{c.item}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {detail.photos?.length > 0 && (
                  <div data-testid="mission-photos-section">
                    <p className="overline-cc mb-2">Job Photos</p>
                    <div className="grid grid-cols-4 gap-2">
                      {detail.photos.map((p) => (
                        <div key={p.id} className="relative rounded-lg overflow-hidden border aspect-square">
                          <img src={p.photo} alt={p.kind} className="w-full h-full object-cover" />
                          <span className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-mono-cc font-bold uppercase ${p.kind === "before" ? "bg-amber-400 text-black" : "bg-emerald-500 text-black"}`}>{p.kind}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="overline-cc mb-2">Timeline</p>
                  <div className="space-y-3 border-l-2 border-border pl-4">
                    {detail.timeline?.slice().reverse().map((t, i) => (
                      <div key={i}>
                        <p>{t.label}</p>
                        <p className="text-[11px] text-muted-foreground font-mono-cc">{new Date(t.ts).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <Button data-testid="mission-delete-button" variant="outline"
                    className="w-full rounded-full text-red-500 border-red-500/40 hover:bg-red-500/10"
                    onClick={() => remove(detail.id)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete mission
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="mission-create-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Mission</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Customer name</Label><Input data-testid="mission-create-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Customer email</Label><Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Service</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Pickup address</Label><Input value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Dropoff address</Label><Input value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} /></div>
            <Button data-testid="mission-create-submit" onClick={createMission} disabled={!form.customer_name} className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">Create Mission</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReceiptUploader
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        missionId={detail?.id}
        missionNumber={detail?.mission_number}
        defaultAmount={detail?.value || 0}
        onDone={() => { setReceiptOpen(false); load(); }}
      />
    </div>
  );
}
